import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Image } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import api, { Company } from '../services/api';

interface CompanySettingsProps {
  onUpdate?: () => void;
}

const CompanySettings: React.FC<CompanySettingsProps> = ({ onUpdate }) => {
  const [company, setCompany] = useState<Company>({
    name: 'Map of Projects',
    primaryColor: '#007bff',
    markerColor: '#007bff'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const { userId } = useParams<{ userId: string }>();

  useEffect(() => {
    loadCompany();
  }, [userId]);

  const loadCompany = async () => {
    try {
      const companyData = await api.getCompany(userId);
      setCompany(companyData);
      if (companyData.logo) {
        setLogoPreview(companyData.logo.url);
      } else if (!companyData.hideLogo) {
        setLogoPreview('/images/landing/default_logo.png');
      }
    } catch (error) {
      console.error('Failed to load company data:', error);
      setMessage({ type: 'error', text: 'Failed to load company settings' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCompany(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = async () => {
    if (window.confirm('Are you sure you want to remove the logo?')) {
      try {
        setLoading(true);
        if (company.logo) {
          // Has a custom uploaded logo — delete it from storage
          const updatedCompany = await api.deleteCompanyLogo(userId);
          setCompany(updatedCompany);
        } else {
          // Using default logo — just hide it
          const formData = new FormData();
          formData.append('name', company.name);
          formData.append('primaryColor', company.primaryColor);
          if (company.markerColor) formData.append('markerColor', company.markerColor);
          formData.append('hideLogo', 'true');
          const updatedCompany = await api.updateCompany(formData, userId);
          setCompany(updatedCompany);
        }
        setLogoFile(null);
        setLogoPreview(null);
        setMessage({ type: 'success', text: 'Logo removed successfully!' });
        if (onUpdate) onUpdate();
      } catch (error) {
        console.error('Error removing logo:', error);
        setMessage({ type: 'error', text: 'Failed to remove logo' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('name', company.name);
      formData.append('primaryColor', company.primaryColor);
      if (company.markerColor) {
        formData.append('markerColor', company.markerColor);
      }

      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const updatedCompany = await api.updateCompany(formData, userId);
      setCompany(updatedCompany);
      setLogoFile(null);

      if (updatedCompany.logo) {
        setLogoPreview(updatedCompany.logo.url);
      }

      setMessage({ type: 'success', text: 'Company settings updated successfully!' });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating company:', error);
      setMessage({ type: 'error', text: 'Failed to update company settings' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        {message && (
          <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
            {message.text}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Title/Company Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={company.name}
                  onChange={handleInputChange}
                  placeholder="Enter title or company name"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Top Bar Color</Form.Label>
                <Form.Control
                  type="color"
                  name="primaryColor"
                  value={company.primaryColor}
                  onChange={handleInputChange}
                  title="Choose primary color for top bar"
                />
                <Form.Text className="text-muted">
                  This color will be used for the navigation bar
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Map Marker Color</Form.Label>
                <Form.Control
                  type="color"
                  name="markerColor"
                  value={company.markerColor || '#ff007b'}
                  onChange={handleInputChange}
                  title="Choose color for map markers"
                />
                <Form.Text className="text-muted">
                  This color will be used for markers on the map
                </Form.Text>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Add Your Company Logo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                />
                <Form.Text className="text-muted">
                  Upload a logo image (PNG, JPG, etc.)
                </Form.Text>
              </Form.Group>

              {logoPreview && (
                <div className="mb-3">
                  <Form.Label>Current Logo Preview</Form.Label>
                  <div>
                    <Image
                      src={logoPreview}
                      alt="Logo preview"
                      style={{ maxHeight: '100px', maxWidth: '200px' }}
                      className="border rounded"
                    />
                    <div className="mt-2">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveLogo}
                        disabled={loading}
                      >
                        Remove Logo
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Updating...' : 'Save Company Settings'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CompanySettings;